/**
 * A SIMULATED MASUNG KIOSK PRINTER, for driving the setup wizard without hardware.
 *
 * It answers the bytes ~/printer/KNOWLEDGE.md says the IP1000 class answers, and — the part that
 * matters — it models the two behaviours that broke the real tills:
 *   - mark feeds DO NOTHING until the paper type is set to black mark (`13 74 44 33 33`);
 *   - stored settings RESTART it: it drops off the bus and comes back as a fresh device.
 * It also keeps a transcript of what it "printed", so a dev can see the slips the wizard produces.
 *
 * Opt-in only (`localStorage['printer-sim'] = '1'` or `?printer=sim`). Never loaded otherwise.
 */

import type { UsbProvider } from './printer';

export interface SimOptions {
  productName?: string;      // 'IP1000 Printer USB001' (ESC/POS QR) or 'Masung Printer' (DC3 QR)
  headDots?: number;         // 576 | 640 | 832
  pitchMm?: number;          // ticket length on the simulated roll
  markMm?: number;           // mark length
  sensorAnswers?: boolean;   // does ESC 0xCC 1 reply
  statusAnswers?: boolean;   // does DLE EOT reply
  gapMm?: number;            // print head to blade
  perfAfterMarkMm?: number;  // where the perforation sits relative to each mark
}

type Listener = (e: any) => void;

export class SimPrinter {
  opts: Required<SimOptions>;
  // Stored ("NV") settings — survive restarts.
  nv = { paperType: 'continuous' as 'mark' | 'gap' | 'continuous', cut: 0, print: 0, threshold: 48 };
  /** Paper travel in dots since power-on. */
  pos = 0;
  lineSpacing = 30;
  /** Print area and alignment as the printer would apply them — so a preview wraps and indents truly. */
  leftDots = 0;
  widthDots: number | null = null;
  align = 0;
  tabs: number[] = [];
  /** GS ! width multiplier — a double-width line holds half the characters. */
  mag = 1;
  paper: string[] = [];
  /**
   * The paper as a physical strip: each printed line at the paper coordinate that was under the
   * head, each cut at the coordinate that was under the BLADE (gap mm earlier). `paperView` merges
   * these with the roll's perforations, so the transcript shows what an operator would see.
   */
  strip: { at: number; text: string; kind: 'line' | 'cut'; mag?: number }[] = [];
  private line = '';
  private replies: number[][] = [];
  private waiters: ((b: number[]) => void)[] = [];
  private buf: number[] = [];
  onChange?: () => void;

  constructor(o: SimOptions = {}) {
    this.opts = {
      productName: o.productName ?? 'IP1000 Printer USB001',
      headDots: o.headDots ?? 576,
      pitchMm: o.pitchMm ?? 152,
      markMm: o.markMm ?? 5,
      sensorAnswers: o.sensorAnswers ?? true,
      statusAnswers: o.statusAnswers ?? true,
      gapMm: o.gapMm ?? 12,
      perfAfterMarkMm: o.perfAfterMarkMm ?? 3,
    };
  }

  private get pitch() { return this.opts.pitchMm * 8; }
  private emit() { this.onChange?.(); }

  private reply(bytes: number[]) {
    const w = this.waiters.shift();
    if (w) w(bytes); else this.replies.push(bytes);
  }
  /** A restart loses whatever was in flight, on both sides of the cable. */
  resetIo() { this.waiters = []; this.replies = []; this.buf = []; this.line = ''; }
  takeReply(): Promise<number[]> {
    const r = this.replies.shift();
    return r ? Promise.resolve(r) : new Promise((res) => this.waiters.push(res));
  }

  private flushLine() {
    const area = Math.min(this.widthDots ?? this.opts.headDots, this.opts.headDots - this.leftDots);
    const chars = Math.max(1, Math.floor(area / (12 * this.mag)));
    const indent = ' '.repeat(Math.floor(this.leftDots / (12 * this.mag)));
    const place = (t: string) => {
      const pad = chars - t.length;
      const body = this.align === 1 ? ' '.repeat(Math.max(0, Math.floor(pad / 2))) + t : this.align === 2 ? ' '.repeat(Math.max(0, pad)) + t : t;
      return indent + body;
    };
    let l = this.line.replace(/\s+$/, '');
    if (!l.length) { this.paper.push(''); }
    let first = true;
    while (l.length) {
      this.paper.push(place(l.slice(0, chars)));
      this.strip.push({ at: this.pos, text: place(l.slice(0, chars)), kind: 'line', mag: this.mag });
      l = l.slice(chars);
      if (!first) this.pos += this.lineSpacing;
      first = false;
    }
    this.line = '';
    this.pos += this.lineSpacing;
  }
  private cut(label = 'CUT') {
    if (this.line) this.flushLine();
    const at = this.pos - this.opts.gapMm * 8;
    this.paper.push(`✂────── ${label} @ ${(at / 8).toFixed(1)}mm ──────`);
    this.strip.push({ at, text: label, kind: 'cut' });
  }

  /** The last `spanMm` of paper as it would come out: lines, cuts and perforations in order. */
  paperView(spanMm = 400): string[] {
    const end = this.pos;
    const start = Math.max(0, end - spanMm * 8);
    const rows: { at: number; text: string }[] = this.strip.filter((e) => e.at >= start).map((e) => ({
      at: e.at, text: e.kind === 'cut' ? `✂✂✂✂✂✂ ${e.text} (${(e.at / 8).toFixed(1)} mm)` : e.text,
    }));
    const perf = this.opts.perfAfterMarkMm * 8;
    for (let k = Math.ceil((start - perf) / this.pitch); k * this.pitch + perf <= end; k++) {
      const at = k * this.pitch + perf;
      if (at >= start) rows.push({ at, text: `· · · · perforation (${(at / 8).toFixed(1)} mm) · · · ·` });
    }
    return rows.sort((a, b) => a.at - b.at).map((r) => r.text);
  }
  private nextMark(after: number) { return Math.ceil((after + 1) / this.pitch) * this.pitch; }

  sensorAD(): number {
    const inTicket = this.pos % this.pitch;
    const onMark = inTicket < this.opts.markMm * 8;
    return (onMark ? 34 : 196) + Math.round((Math.random() - 0.5) * 8);
  }

  /** Parse one write. Returns 'restart' if a stored setting was written. */
  feed(data: Uint8Array): 'ok' | 'restart' {
    this.buf.push(...data);
    let restart = false;
    const b = this.buf;
    const need = (n: number) => b.length >= n;
    while (b.length) {
      const c = b[0];
      let used = 1;
      if (c === 0x1B && need(2)) {
        const k = b[1];
        if (k === 0x40) { this.lineSpacing = 30; this.leftDots = 0; this.widthDots = null; this.align = 0; this.mag = 1; used = 2; }
        else if (k === 0x61 && need(3)) { this.align = b[2]; used = 3; }
        else if ([0x79, 0x47, 0x56, 0x7B, 0x2D, 0x21, 0x52, 0x74].includes(k)) used = 3;
        else if (k === 0x33 && need(3)) { this.lineSpacing = b[2]; used = 3; }
        else if (k === 0x4A && need(3)) { if (this.line) this.flushLine(); this.pos += b[2]; used = 3; }
        else if (k === 0x4B && need(3)) { this.pos -= b[2]; used = 3; }
        else if (k === 0x64 && need(3)) { if (this.line) this.flushLine(); for (let i = 0; i < b[2]; i++) this.flushLine(); used = 3; }
        else if (k === 0x44) { const z = b.indexOf(0, 2); if (z < 0) break; this.tabs = b.slice(2, z); used = z + 1; }
        else if (k === 0x25 && need(3)) used = 3;
        else if (k === 0x20 && need(3)) used = 3;
        else if (k === 0x69 || k === 0x6D) { this.cut(); used = 2; }
        else if (k === 0x0C) { this.markFeed('print'); used = 2; }
        else if (k === 0xCC && need(3)) { if (this.opts.sensorAnswers) this.reply([this.sensorAD()]); used = 3; }
        else if (!need(3)) break;
        else used = 2;
      } else if (c === 0x1D && need(2)) {
        const k = b[1];
        if (k === 0x0C) { this.markFeed('cut'); used = 2; }
        else if (k === 0x4C && need(4)) { this.leftDots = b[2] | (b[3] << 8); used = 4; }
        else if (k === 0x57 && need(4)) { this.widthDots = b[2] | (b[3] << 8); used = 4; }
        else if (k === 0x21 && need(3)) { this.mag = (b[2] >> 4) + 1; used = 3; }
        else if ([0x42, 0x50, 0x77, 0x68, 0x66, 0x48].includes(k)) used = 3;
        else if (k === 0x49 && need(3)) { this.replyInfo(b[2]); used = 3; }
        else if (k === 0x72 && need(3)) { if (this.opts.statusAnswers) this.reply([0x00]); used = 3; }
        else if (k === 0x56 && need(4)) { if (this.nv.paperType !== 'continuous') this.markFeed('cut'); this.cut('MARK CUT'); used = 4; }
        else if (k === 0x28 && need(5)) {
          const len = b[3] | (b[4] << 8);
          if (!need(5 + len)) break;
          if (b[2] === 0x41) { this.selfTest(); }
          else if (b[2] === 0x6B && b[6] === 0x51) {
            if (this.line) this.flushLine();
            if (this.escpos) { this.paper.push('▓▓ QR (standard) ▓▓'); this.strip.push({ at: this.pos, text: '▓▓ QR (standard) ▓▓', kind: 'line' }); this.pos += 200; } else this.paper.push('(1D 28 6B printed as junk text)');
          }
          used = 5 + len;
        } else if (k === 0x76 && need(8)) {
          const w = b[4] | (b[5] << 8), h = b[6] | (b[7] << 8);
          if (!need(8 + w * h)) break;
          if (this.line) this.flushLine();
          if (this.escpos) { this.paper.push(`▒▒ picture ${w * 8}×${h} ▒▒`); this.pos += h; } else this.paper.push('(picture: nothing printed)');
          used = 8 + w * h;
        } else if (k === 0x6B && need(4)) {
          // GS k m: m 0..8 is NUL-terminated; m ≥ 65 carries a length byte instead.
          if (b[2] >= 65) { if (!need(4 + b[3])) break; used = 4 + b[3]; }
          else { const z = b.indexOf(0, 3); if (z < 0) break; used = z + 1; }
          if (this.line) this.flushLine();
          this.paper.push('||| barcode |||'); this.strip.push({ at: this.pos, text: '||| barcode |||', kind: 'line' }); this.pos += 60;
        }
        else used = 2;
      } else if (c === 0x10 && need(3)) {
        if (b[1] === 0x04) { if (this.opts.statusAnswers) this.reply([this.status(b[2])]); }
        used = 3;
      } else if (c === 0x12 && need(3) && b[1] === 0x53 && b[2] === 0x41) { this.reply([0x12, 0x53, 0x41, 0x2A]); used = 3; }
      else if (c === 0x13 && need(2)) {
        const k = b[1];
        if (k === 0x74 && need(4)) {
          const g = b[2], p = b[3];
          if (p === 0x78) { if (!need(6)) break; const v = (b[4] << 8) | b[5]; if (g === 0x33) this.nv.cut = v; else this.nv.print = v; used = 6; restart = true; }
          else {
            if (!need(5)) break;
            if (g === 0x44 && p === 0x33) this.nv.paperType = b[4] === 0x33 ? 'mark' : b[4] === 0x35 ? 'gap' : 'continuous';
            if (g === 0x11 && p === 0x66) this.nv.threshold = b[4];
            used = 5; restart = true;
          }
        } else if (k === 0x50 && need(4)) {
          // DC3 P H n ... — QR. Sub-command 04 carries `01 lenH lenL data`.
          if (b[3] === 0x04) { if (!need(7)) break; const len = (b[5] << 8) | b[6]; if (!need(7 + len)) break; used = 7 + len; }
          else if (b[3] === 0x05) { used = 4; if (this.line) this.flushLine(); if (!this.escpos) { this.paper.push('▓▓ QR (Masung) ▓▓'); this.strip.push({ at: this.pos, text: '▓▓ QR (Masung) ▓▓', kind: 'line' }); this.pos += 200; } else this.paper.push('(Masung QR: garbage left in buffer)'); }
          else used = 5;
        } else used = 2;
      } else if (c === 0x1C && need(2)) used = 2;
      else if (c === 0x0A) this.flushLine();
      else if (c === 0x0C) this.markFeed('mark');
      else if (c === 0x09) { const next = this.tabs.find((t) => t > this.line.length); this.line = next ? this.line.padEnd(next, ' ') : this.line + ' '; }
      else if (c >= 0x20 && c < 0x7F) this.line += String.fromCharCode(c);
      else if (c >= 0x80) this.line += '?';
      else if ([0x1B, 0x1D, 0x10, 0x12, 0x13, 0x1C].includes(c)) break; // incomplete prefix — wait for more
      b.splice(0, used);
    }
    this.emit();
    return restart ? 'restart' : 'ok';
  }

  private get escpos() { return this.opts.productName.toLowerCase().includes('ip1000'); }

  private markFeed(kind: 'print' | 'cut' | 'mark') {
    if (this.line) this.flushLine();
    if (this.nv.paperType === 'continuous') return; // the bug every till hit: accepted, ignored
    const off = kind === 'cut' ? this.nv.cut : kind === 'print' ? this.nv.print : 0;
    let target = this.nextMark(this.pos - off) + off;
    if (target <= this.pos) target += this.pitch;
    this.pos = target;
  }

  /** Force DLE EOT replies (dev): e.g. [0x1e,0x52,0x16,0x13,0x10] = the EP802's "notch not found". */
  statusOverride: number[] | null = null;
  private status(n: number): number {
    if (this.statusOverride) return this.statusOverride[n - 1] ?? 0;
    switch (n) {
      case 1: return 0x16;
      case 2: return 0x12;
      case 3: return 0x12;
      case 4: return 0x12;
      case 5: return 0x10;
      default: return 0;
    }
  }

  private replyInfo(n: number) {
    const s = ['', '1', 'MS532I0', 'V2.10', 'MASUNG', this.opts.productName.replace(/ USB\d+$/, ''), 'GBK'][n];
    if (n === 7) { this.reply([0x5A, 0x3C]); return; }
    this.reply(Array.from(new TextEncoder().encode(s ?? '')));
  }

  private selfTest() {
    if (this.line) this.flushLine();
    this.paper.push(
      '*** SELF TEST ***', `Model: ${this.opts.productName}`, 'Firmware: V2.10',
      `Paper type: ${this.nv.paperType === 'mark' ? 'Black mark' : this.nv.paperType === 'gap' ? 'Label' : 'Continuous'}`,
      `Black mark cut offset: ${this.nv.cut}`, `Black mark print offset: ${this.nv.print}`,
      `Black mark AD: ${this.nv.threshold}`, 'Baud: 115200', '*****************',
    );
    this.pos += 9 * 30;
    this.cut('SELF TEST');
  }
}

/** A fake `navigator.usb` holding one SimPrinter that re-enumerates on stored-setting writes. */
export function createSimulatedUsb(sim: SimPrinter): UsbProvider {
  const listeners: Listener[] = [];
  let present = true;
  let current = makeDevice();

  function makeDevice(): USBDevice {
    let opened = false, gone = false;
    const dev: any = {
      vendorId: 0x0519, productId: 0x2013,
      productName: sim.opts.productName, manufacturerName: 'MASUNG', serialNumber: 'SIM-MS532I0',
      configuration: {
        interfaces: [{
          interfaceNumber: 0,
          alternate: {
            interfaceClass: 7, interfaceProtocol: 2,
            endpoints: [
              { endpointNumber: 3, direction: 'out', type: 'bulk' },
              { endpointNumber: 1, direction: 'in', type: 'bulk' },
            ],
          },
        }],
      },
      get opened() { return opened; },
      async open() { if (gone) throw new Error('device gone'); opened = true; },
      async close() { opened = false; },
      async selectConfiguration() {},
      async claimInterface() { if (gone) throw new Error('device gone'); },
      async releaseInterface() {},
      async clearHalt() {},
      async transferOut(_ep: number, data: BufferSource) {
        if (gone) throw new Error('The device was disconnected.');
        const bytes = data instanceof Uint8Array ? data : new Uint8Array(data as ArrayBuffer);
        await new Promise((r) => setTimeout(r, 2));
        if (sim.feed(bytes) === 'restart') restartSoon();
        return { status: 'ok', bytesWritten: bytes.length };
      },
      async transferIn() {
        if (gone) throw new Error('The device was disconnected.');
        const bytes = await sim.takeReply();
        const u = new Uint8Array(bytes);
        return { status: 'ok', data: new DataView(u.buffer) };
      },
      _kill() { gone = true; },
    };
    return dev as USBDevice;
  }

  function restartSoon() {
    setTimeout(() => {
      const old = current as any;
      old._kill();
      present = false;
      listeners.forEach((l) => l({ device: old }));
      sim.resetIo();
      setTimeout(() => { current = makeDevice(); present = true; }, 1500);
    }, 150);
  }

  return {
    async requestDevice() { if (!present) throw Object.assign(new Error('No device selected.'), { name: 'NotFoundError' }); return current; },
    async getDevices() { return present ? [current] : []; },
    addEventListener(_t, l) { listeners.push(l as Listener); },
    removeEventListener(_t, l) { const i = listeners.indexOf(l as Listener); if (i >= 0) listeners.splice(i, 1); },
  };
}

export const simEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('printer');
    if (q === 'sim') localStorage.setItem('printer-sim', '1');
    if (q === 'real') localStorage.removeItem('printer-sim');
    return localStorage.getItem('printer-sim') === '1';
  } catch { return false; }
};

let installed: SimPrinter | null = null;
/** The simulated printer in use, if any — the wizard shows its paper transcript. */
export const activeSim = () => installed;

export function installSimIfEnabled(printer: { useUsb(p: UsbProvider | null): void }) {
  if (installed || !simEnabled()) return installed;
  let opts: SimOptions = {};
  try { opts = JSON.parse(localStorage.getItem('printer-sim-opts') || '{}'); } catch {}
  installed = new SimPrinter(opts);
  printer.useUsb(createSimulatedUsb(installed));
  // Dev handle for poking at the simulated pair from the console.
  (window as any).__tktPrinter = printer;
  (window as any).__tktSim = installed;
  return installed;
}
